import React from 'react';
import clsx from 'clsx';
import { Button, makeStyles, ButtonProps } from '@material-ui/core';
import { Theme } from '@material-ui/core/styles';

const useStyles = makeStyles((theme: Theme) => {
  return {
    root: {
      '&:disabled': {
        color: theme.palette2.text.paper,
        opacity: 0.5
      },
      fontSize: 12,
      [theme.breakpoints.up('md')]: {
        fontSize: 14
      },
      '&.primary': {
        backgroundColor: theme.palette2.background.primary,
        color: theme.palette2.text.paper,
        '&:hover': {
          backgroundColor: theme.palette2.background.primary
        }
      },
      '&.secondary': {
        backgroundColor: theme.palette2.background.secondary,
        color: theme.palette2.text.paper,
        '&:hover': {
          backgroundColor: theme.palette2.background.secondary
        }
      },
      '&.secondary-outlined': {
        backgroundColor: theme.palette2.text.paper,
        color: theme.palette2.background.secondary,
        border: `1px solid ${theme.palette2.background.secondary}`,
        '&:hover': {
          backgroundColor: theme.palette2.text.paper
        }
      }
    }
  };
});

interface AppButtonProps extends Omit<ButtonProps, 'color'> {
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  endIcon?: React.ReactNode;
  fullWidth?: boolean;
  startIcon?: React.ReactNode;
  variant?: 'contained' | 'outlined' | 'text';
  disableElevation?: boolean;
  disableFocusRipple?: boolean;
  disableRipple?: boolean;
  href?: string;
  size?: 'small' | 'medium' | 'large';
  onClick?: React.MouseEventHandler<HTMLButtonElement>;
  component?: React.ElementType;
  to?: string;
  color?: 'inherit' | 'default' | 'primary' | 'secondary';
}

const AppButton: React.FC<AppButtonProps> = ({
  className = '',
  children,
  disabled = false,
  disableElevation = false,
  disableFocusRipple = false,
  disableRipple = false,
  href,
  size = 'medium',
  endIcon,
  fullWidth = true,
  startIcon,
  variant = 'text',
  onClick,
  color,
  ...rest
}) => {
  const classes = useStyles();
  return (
    <Button
      className={clsx(classes.root, className)}
      disabled={disabled}
      endIcon={endIcon}
      fullWidth={fullWidth}
      startIcon={startIcon}
      variant={variant}
      disableElevation={disableElevation}
      disableFocusRipple={disableFocusRipple}
      disableRipple={disableRipple}
      href={href}
      size={size}
      onClick={onClick}
      color={color}
      {...rest}
    >
      {children}
    </Button>
  );
};

export default AppButton;
